import { type ReactNode, useState } from "react";

import QuantitySelector from "common/components/subscriptions/QuantitySelector";
import Alerts from "common/components/subscriptions/RentalSubscription/Alerts";
import PricingDetails from "common/components/subscriptions/RentalSubscription/PricingDetails";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useRentalMutations from "common/datahooks/useRentalMutations";
import useSubscriptionMutations from "common/datahooks/useSubscriptionMutations";
import { trackMetaPixel } from "common/helpers/utils";
import type { Pricing } from "common/types";

const MAX_ACCOUNTS = 999;

export default function RentalSubscription({
  children,
  currentStep,
  onSuccess,
  pricing,
  minQuantity,
}: {
  children: (params: {
    isNextStepDisabled: boolean;
    isNextButtonLoading: boolean;
    onCreateSubscription: (paymentSourceId: string) => void;
    mainContent: ReactNode;
  }) => ReactNode;
  currentStep: "manage" | "summary" | "payment" | "cancelAccounts" | "success";
  onSuccess: () => void;
  pricing: { addon: Pricing; plan: Pricing };
  minQuantity: number;
}) {
  const { rental_subscription: currentSubscription } =
    useSelectedWorkspaceContext();

  let minAccounts =
    currentSubscription && currentSubscription.status === "active" ? 0 : 1;
  if (minQuantity) {
    minAccounts = Math.max(minAccounts, minQuantity);
  }

  const [accountCounts, setAccountsCount] = useState<number>(
    currentSubscription
      ? Math.max(minAccounts, currentSubscription.rented_count)
      : minAccounts,
  );
  const { addRentalAccounts, isAddingRentalAccounts } = useRentalMutations();
  const { createSubscription, isCreatingSubscription } =
    useSubscriptionMutations();

  async function onCreateSubscription(paymentSourceId: string) {
    let subscriptionId: string;

    if (currentSubscription) {
      await addRentalAccounts({
        rentalCount: accountCounts,
        planPriceId: pricing.plan.period === 6 ? pricing.plan.id : undefined,
      });

      subscriptionId = currentSubscription.id;
    } else {
      const { id } = await createSubscription({
        count: accountCounts,
        type: "rental",
        planId: pricing.plan.id,
        paymentSourceId,
      });

      subscriptionId = id;
    }

    const eventData = {
      value: pricing.plan.price * accountCounts,
      currency: "USD",
      content_type: "subscription",
      content_ids: [subscriptionId],
      num_items: accountCounts,
    };

    trackMetaPixel("track", "Subscribe", eventData);
    trackMetaPixel("track", "Purchase", eventData);

    onSuccess();
  }

  //all the limits below should not apply for subscriptions that are cancelled
  const isDowngrade =
    currentSubscription?.status === "active" &&
    accountCounts < currentSubscription.rented_count;

  const isUnchangedSubscription =
    currentSubscription?.status === "active"
      ? currentSubscription.rented_count === accountCounts &&
        currentSubscription.item_price.id === pricing.plan.id
      : false;

  const pricingDetailsContent = (
    <PricingDetails
      pricing={pricing}
      isUnchangedSubscription={isUnchangedSubscription}
      isDowngrade={isDowngrade}
      accountCounts={accountCounts}
      alertsComponent={
        (currentStep === "summary" || currentStep === "manage") && (
          <Alerts
            pricing={pricing}
            currentStep={currentStep}
            isUnchangedSubscription={isUnchangedSubscription}
            isDowngrade={isDowngrade}
          />
        )
      }
    />
  );

  const mainContent =
    currentStep === "manage" ? (
      <>
        <QuantitySelector
          quantity={accountCounts}
          setQuantity={setAccountsCount}
          minValue={minAccounts}
          maxValue={MAX_ACCOUNTS}
        />
        <div className="flex flex-col gap-y-2.5">{pricingDetailsContent}</div>
      </>
    ) : (
      pricingDetailsContent
    );

  return children({
    isNextStepDisabled: isDowngrade || isUnchangedSubscription,
    isNextButtonLoading: isAddingRentalAccounts || isCreatingSubscription,
    onCreateSubscription,
    mainContent,
  });
}
